import React, { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import RibbonNavbar from "../../components/RibbonNavbar";
import SearchInput from "../../components/SearchInput";
import { iepaProd } from "../../constants/global";
import SearchableDataGrid from '../../components/SearchableDataGrid';

const OrderList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // ----------------------------------------------------------------------
  // Fetch Orders
  // ----------------------------------------------------------------------
  const fetchOrders = async () => {
    try {
      const response = await fetch(
        `${iepaProd.iepaOrderMgmtAPI}/orm/order/entered/org/1`
      );
      const data = await response.json();

      setOrders(data);
      setRows(data);
    } catch (error) {
      setError("Failed to load orders.");
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ----------------------------------------------------------------------
  // Date Formatter
  // ----------------------------------------------------------------------
  const formatDate = (date = "") => date.split("T")[0];

  // ----------------------------------------------------------------------
  // Approve Orders API
  // ----------------------------------------------------------------------
  const approveOrders = async (orderData) => {
    try {
      await fetch(`${iepaProd.iepaOrderMgmtAPI}/orm/order/approve/org/1`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderData),
      });

      setMessage("Orders approved successfully!");
      fetchOrders();
    } catch (error) {
      setError("Failed to approve orders.");
    }
  };

  const handleApproveOrders = () => {
    if (selectedRows.length === 0) return;

    const payload = {
      updateOrderList: selectedRows,
      createdBy: 1,
    };

    approveOrders(payload);
  };

  // ----------------------------------------------------------------------
  // Filtering Orders
  // ----------------------------------------------------------------------
  const filteredRows = rows.filter((order) =>
    Object.values(order)
      .join(" ")
      .toLowerCase()
      .includes(filterText.toLowerCase())
  );

  // ----------------------------------------------------------------------
  // DataGrid Column Definitions
  // ----------------------------------------------------------------------
  const columns = [
    { field: "orderNumber", headerName: "Order ID", flex: 1 },
    {
      field: "requestDate",
      headerName: "Date",
      flex: 1,
      valueFormatter: (params) => formatDate(params),
    },
    { field: "customerName", headerName: "Customer Name", flex: 1 },
    { field: "totalAmount", headerName: "Estimated Value", flex: 1 },
    { field: "status", headerName: "Status", flex: 1 },

    {
      field: "view",
      headerName: "View Estimate",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="contained"
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: "white",
            fontSize: "12px",
          }}
          onClick={() =>
            navigate("/invoice", {
              state: {
                selectedCustomer: null,
                selectedProducts: null,
                orderNumber: params.id,
              },
            })
          }
        >
          View
        </Button>
      ),
    },
  ];

  // ----------------------------------------------------------------------
  // Component Render
  // ----------------------------------------------------------------------
  return (
    <Box m="20px">
      {/* Header + Search + Ribbon Navbar -------------------------------- */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Manage Orders" marginBottom="0" />        

        <RibbonNavbar
          selectedRows={selectedRows}
          onAdd={() => navigate("../TakeOrder")}
          onEdit={() => navigate("../ModifyOrder", { state: { selectedRows } })}
          onApprove={handleApproveOrders}
        />

      </Box>

      {/* DataGrid --------------------------------------------------------- */}
      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid grey",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
            fontSize: "14px",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <SearchableDataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.orderNumber}
          checkboxSelection
          components={{ Toolbar: GridToolbar }}
          onRowSelectionModelChange={(selected) => setSelectedRows(selected)}
        />
      </Box>

      {/* Messages --------------------------------------------------------- */}
      {message && <Typography color="success.main">{message}</Typography>}
      {error && <Typography color="error.main">{error}</Typography>}
    </Box>
  );
};

export default OrderList;
